import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { CaseDetailContext } from './CaseDetailContext'
import api from '../../utils/api'
import CaseActivityLog from '../activities/CaseActivityLog'
import { setCachedCaseActivities } from '../activities/caseActivitiesCache'

/**
 * ActivityLog — right-sidebar collapsible log of email/communication
 * events per party. Mirrors the screenshot's "Plaintiff: {name}" and
 * "Defendant: {name}" collapsible cards.
 *
 * Each card lists events from `caseData.emailLogs` (a.k.a. activityLogs)
 * filtered by client_id, formatted as "{event} — {short date}".
 */

function ChevronDown({ open }) {
    return (
        <svg
            className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    )
}

function fmtShortDate(s) {
    if (!s) return ''
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return ''
    // Activity log: short "May 03" matches Scale's compact log layout. These are
    // timestamptz events — project to Pacific (matches Scale) so a late-Pacific
    // event isn't shown on the next day for non-Pacific viewers.
    return d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: '2-digit' })
}

function PartyLog({ title, logs, defaultOpen, activities = [] }) {
    const [open, setOpen] = useState(defaultOpen)
    return (
        <div className="border-b last:border-b-0">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50"
            >
                <span className="text-sm font-medium text-gray-900">{title}</span>
                <ChevronDown open={open} />
            </button>
            {open && (
                <div className="px-5 pt-1 pb-4">
                    {logs.length === 0 && activities.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">No activity yet.</p>
                    ) : (
                        // Vertical rhythm: each entry gets py-3 of internal
                        // padding, separated by a divider line (no margin
                        // collapse). divide-y handles the separator; py-3
                        // gives both the line above and below room to breathe.
                        <ul className="divide-y divide-gray-100">
                            {logs
                                .slice()
                                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                .slice(0, 30)
                                .map((log, i) => (
                                    <li
                                        key={log.id || i}
                                        className="flex items-start justify-between gap-3 py-3 first:pt-1 last:pb-1"
                                    >
                                        <div className="flex-1 min-w-0 space-y-1">
                                            <p className="text-[13px] leading-5 text-gray-800 font-medium break-words">
                                                {log.event || log.type || 'Activity'}
                                            </p>
                                            {log.amount != null && (
                                                <p className="text-xs leading-4 text-gray-500">Amount: ${log.amount}</p>
                                            )}
                                            {log.next_reminder && (
                                                <p className="text-xs leading-4 text-gray-500">
                                                    {log.next_reminder === 'No further reminders'
                                                        ? log.next_reminder
                                                        : `Next Reminder: ${log.next_reminder}`}
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-xs leading-5 text-gray-400 shrink-0 mt-0.5">
                                            {fmtShortDate(log.created_at)}
                                        </span>
                                    </li>
                                ))}
                        </ul>
                    )}
                    {/* THE CASE'S ACTIVITIES, read-only, under the party's own
                        events — the same list under every party, because an
                        activity belongs to the CASE and the mediator, never to
                        the plaintiff or to Defendant 2. There is no party
                        column on the row and no way to derive one, so rather
                        than invent an owner the case-level log shows in each
                        section, the way scale-portal's tracker does. */}
                    <CaseActivityLog activities={activities} />
                </div>
            )}
        </div>
    )
}

export default function ActivityLog() {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel } = useContext(CaseDetailContext)
    const plaintiff = caseData?.plaintiff
    const defendant = caseData?.defendant
    // Scale parity (TrackerLogs.jsx): each party object carries its own
    // `email_logs` array, populated by the BE via the plaintiff_id /
    // defender_id / additional_client_id buckets on the email_logs row.
    const plaintiffLogs = useMemo(
        () => plaintiff?.email_logs || plaintiff?.emailLogs || [],
        [plaintiff]
    )
    const defendantLogs = useMemo(
        () => defendant?.email_logs || defendant?.emailLogs || [],
        [defendant]
    )
    const plaintiffAddl = plaintiff?.additionalParties || []
    const defendantAddl = defendant?.additionalParties || []

    /* The form promises "Add to your calendar and case timeline" — this is the
     * case timeline half. Gated on activity_available, which the server
     * resolves from the mediator's booking_features and fails closed; a failed
     * load must not take the party logs down with it. */
    const activityEnabled = caseData?.activity_available === true
    const activityMediatorId =
        caseData?.mediator?.userId || caseData?.mediator?.user_id
    const [activities, setActivities] = useState([])

    const loadActivities = useCallback(async () => {
        if (!activityEnabled || !caseData?.id || !activityMediatorId) {
            setActivities([])
            return
        }
        try {
            const res = await api.get('/activities', {
                params: { mediatorId: activityMediatorId, caseId: caseData.id },
            })
            const rows = res?.data?.data || []
            setActivities(rows)
            /* So the Activities dialog on this page opens from these rows
               rather than repeating the identical request. */
            setCachedCaseActivities(caseData.id, rows)
        } catch (err) {
            console.error('case activities load failed', err)
            setActivities([])
        }
    }, [activityEnabled, activityMediatorId, caseData?.id])

    useEffect(() => {
        loadActivities()
    }, [loadActivities])

    if (!plaintiff?.client_id && !defendant?.client_id) return null

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {plaintiff?.client_id && (
                <PartyLog
                    title={`${plaintiffPartyLabel}: ${plaintiff.name || ''}`}
                    logs={plaintiffLogs}
                    defaultOpen
                activities={activities}
                />
            )}
            {plaintiffAddl.map((p) => (
                <PartyLog
                    key={`pa-${p.client_id || p.id}`}
                    title={`${p.position || p.role || 'Additional'}: ${p.name || ''}`}
                    logs={p.email_logs || p.emailLogs || []}
                    defaultOpen={false}
                activities={activities}
                />
            ))}
            {defendant?.client_id && (
                <PartyLog
                    title={`${defendantPartyLabel}: ${defendant.name || ''}`}
                    logs={defendantLogs}
                    defaultOpen={false}
                activities={activities}
                />
            )}
            {defendantAddl.map((p) => (
                <PartyLog
                    key={`da-${p.client_id || p.id}`}
                    title={`${p.position || p.role || 'Additional'}: ${p.name || ''}`}
                    logs={p.email_logs || p.emailLogs || []}
                    defaultOpen={false}
                activities={activities}
                />
            ))}
        </div>
    )
}
